import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { emitGAEventSetUserId, emitHomePageViewGAEvent } from 'utils/ga-events-helpers';

// Emit `home_page_view` once per home/landing-page mount, deferred until the current-session
// GA4 User-ID is available so the event — and the `session_start` / `first_visit` that GA4
// derives from the session's first event — carries the SAME `user_id` as `search_submission`,
// letting the view→search funnel join on user_id.
//
// The id (uid/imid) comes from Redux `state.base`, populated when `init('hotels')` resolves. It
// is NOT redux-persisted, so it is always this session's id. We deliberately do NOT read the
// persisted `tpdrops-uid` / `tpdrops-imid` from localStorage: those rotate per session, so the
// stored value is a STALE previous-session id that would mismatch search_submission's current id
// (~35-44% of returning-user sessions, measured in BigQuery) and break the join.
//
// A 2s timeout guarantees the event still fires (without user_id) if `init` is slow or fails —
// the funnel event is never dropped. The ref de-dupes the emit (e.g. React StrictMode's
// double-invoke in dev).
//
// This replaces the old fire-on-every-route emit in `_app.tsx`, which fired on the `window
// 'load'` event — racing the listener so a large share of sessions never sent it. Mounting this
// hook only in the search-form pages scopes the event to genuine home/landing views.
export const useHomePageViewGAEvent = (enabled: boolean = true) => {
	const uid = useSelector((state: { base: { uid?: string | null } }) => state.base.uid);
	const imid = useSelector((state: { base: { imid?: string | null } }) => state.base.imid);
	const firedRef = useRef(false);

	useEffect(() => {
		if (!enabled || firedRef.current) return;

		const fire = () => {
			if (firedRef.current) return;
			firedRef.current = true;
			// Attach the current-session User-ID (built exactly like baseActions.init's set) so
			// home_page_view carries the same user_id as search_submission.
			const userId = [uid, imid].filter(Boolean).join('') || undefined;
			if (userId) {
				emitGAEventSetUserId(userId);
			}
			emitHomePageViewGAEvent();
		};

		// Defer on the current-session `uid` from Redux (populated by init()) for ALL users.
		if (uid) {
			fire();
			return;
		}

		// init() slow or failed → fire without user_id rather than dropping the funnel event.
		const timeoutId = setTimeout(fire, 2000);
		return () => clearTimeout(timeoutId);
	}, [uid, imid, enabled]);
};
